import ons from "onsenui";
import * as React from "react";
import * as Ons from "react-onsenui";
import ReactSignatureCanvas from "react-signature-canvas";
import { deepEqual } from "../deep-equal";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { getNotchCss } from "../utils/DeviceUtils";
import { AppOverlay } from "./Generic/AppOverlay";

interface AnatomyCanvasProps {
  onSave: (data: string) => void;
  initialData?: string;
  backgroundImage: string;
  imageHeight: number;
  imageWidth: number;
  onCancel: () => void;
  readonly: boolean;
}

interface AnatomyCanvasState {
  changeStack: string[];
}

export class AnatomyCanvas extends React.Component<AnatomyCanvasProps, AnatomyCanvasState> {
  constructor(props: AnatomyCanvasProps) {
    super(props);

    this.state = {
      changeStack: [],
    };
  }

  shouldComponentUpdate(_props: AnatomyCanvasProps, state: AnatomyCanvasState) {
    return !deepEqual(this.state.changeStack, state.changeStack);
  }

  componentDidMount() {
    if (this.props.initialData !== undefined) {
      const initialDataImage = new Image();

      initialDataImage.onload = () => {
        this.signature.getCanvas().getContext("2d").drawImage(initialDataImage, 0, 0);
      };

      initialDataImage.src = this.props.initialData;
    } else {
      this.signature.clear();
    }
  }

  signature: any = null;

  handleChange = () => {
    const newSignature = this.signature.isEmpty() ? null : this.signature.getCanvas().toDataURL();
    if (
      newSignature !== null &&
      (this.state.changeStack.length === 0 ||
        this.state.changeStack[this.state.changeStack.length - 1] !== newSignature)
    ) {
      this.setState((state: AnatomyCanvasState) => {
        state.changeStack.push(newSignature);
      });
    }
  };

  handleUndo() {
    if (this.state.changeStack.length > 0) {
      if (this.state.changeStack.length === 1) {
        this.signature.clear();
      } else {
        const previousImage = new Image();

        previousImage.onload = () => {
          this.signature.clear();
          this.signature.getCanvas().getContext("2d").drawImage(previousImage, 0, 0);
        };

        previousImage.src = this.state.changeStack[this.state.changeStack.length - 2];
      }

      this.setState((state: AnatomyCanvasState) => {
        state.changeStack.pop();
      });
    }
  }

  getCanvasSizes() {
    const IMAGE_MAX_HEIGHT = this.props.imageHeight;
    const IMAGE_MAX_WIDTH = this.props.imageWidth;
    const HEADER_AND_FOOTER_HEIGHT = 150;

    const windowHeight = (window as any).innerHeight - HEADER_AND_FOOTER_HEIGHT;
    const windowWidth = (window as any).innerWidth;

    const heightRatio = windowHeight / IMAGE_MAX_HEIGHT < 1 ? windowHeight / IMAGE_MAX_HEIGHT : 1;
    const widthRatio = windowWidth / IMAGE_MAX_WIDTH < 1 ? windowWidth / IMAGE_MAX_WIDTH : 1;

    const result = { height: IMAGE_MAX_HEIGHT, width: IMAGE_MAX_WIDTH };

    const smallestRatio = Math.min(heightRatio, widthRatio);

    result.height *= smallestRatio;
    result.width *= smallestRatio;

    return result;
  }

  handleCancel() {
    if (this.state.changeStack.length === 0) {
      this.props.onCancel();
    } else {
      ons.notification
        .confirm(translate(STRINGS.LEAVE_WITHOUT_SAVING), {
          animation: "none",
          modifier: "material",
        })
        .then((isPositive: HTMLElement) => {
          if (isPositive as any) {
            this.props.onCancel();
          }
        });
    }
  }

  handleClear() {
    this.setState((state: AnatomyCanvasState) => {
      state.changeStack.push("");
    });
    this.signature.clear();
  }

  handleSave() {
    const backgroundImage = new Image();
    const drawingImage = new Image();
    backgroundImage.crossOrigin = "anonymous";
    drawingImage.crossOrigin = "anonymous";

    const redrawBackground = () => {
      const size = this.getCanvasSizes();
      this.signature
        .getCanvas()
        .getContext("2d")
        .drawImage(backgroundImage, 0, 0, size.width, size.height);

      drawingImage.src = this.state.changeStack[this.state.changeStack.length - 1];
    };

    const redrawDrawing = () => {
      this.signature.getCanvas().getContext("2d").drawImage(drawingImage, 0, 0);

      this.props.onSave(this.signature.getCanvas().toDataURL());
    };

    backgroundImage.onload = redrawBackground;
    backgroundImage.src = this.props.backgroundImage;
    drawingImage.onload = redrawDrawing;
  }

  render() {
    const size = this.getCanvasSizes();
    return (
      <AppOverlay>
        <div className={"AnatomyCanvas__root"}>
          <ReactSignatureCanvas
            clearOnResize={false}
            canvasProps={{
              style: {
                pointerEvents: this.props.readonly ? "none" : "auto",
                backgroundImage: "url(" + this.props.backgroundImage + ")",
              },
              height: size.height,
              width: size.width,
              className: `AnatomyCanvas__canvas ${getNotchCss("AnatomyCanvas__canvas_iOS", "")}`,
            }}
            ref={(e: any) => (this.signature = e)}
            onEnd={() => this.handleChange()}
          />
          {!this.props.readonly
            ? [
                <Ons.Button
                  key="anatomy-undo-button"
                  onClick={() => this.handleUndo()}
                  className="AnatomyCanvas__undo"
                >
                  {translate(STRINGS.UNDO)}
                </Ons.Button>,
                <Ons.Button
                  key="anatomy-save-button"
                  onClick={() =>
                    this.state.changeStack.length > 0 ? this.handleSave() : this.props.onCancel()
                  }
                  className="AnatomyCanvas__save"
                >
                  {translate(STRINGS.SAVE)}
                </Ons.Button>,
                <Ons.Button
                  key="anatomy-clear-button"
                  onClick={() => this.handleClear()}
                  className="AnatomyCanvas__clear"
                  style={{ top: ons.platform.isIOS() ? 35 : undefined }}
                >
                  {translate(STRINGS.CLEAR)}
                </Ons.Button>,
              ]
            : ""}
          <div
            className="AnatomyCanvas__back"
            onClick={() => this.handleCancel()}
            style={{ top: ons.platform.isIOS() ? 44 : undefined }}
          />
        </div>
      </AppOverlay>
    );
  }
}
